// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Knowledge Base View Configuration
 *
 * Strategy pattern for KB variants. Allows different nodes (Findings, Report)
 * to share the data layer while customizing the view behavior.
 *
 * Design:
 * - Data layer (KnowledgeBaseProvider) is shared across all variants
 * - View config controls what's shown and how items are created
 * - Custom components can be injected for highly divergent UIs
 */
import type { FC } from 'react'

import type { KnowledgeBaseItem } from './knowledgeBase.types'
import type { KnowledgeBaseSettings } from '../hooks/useKnowledgeBaseSettings'

/**
 * Configuration for a KB view variant.
 * Defines display strings, feature flags, and default values.
 */
export interface KnowledgeBaseViewConfig {
  /**
   * Variant identifier for debugging and logging.
   */
  variant: 'findings' | 'report'

  /**
   * Display strings for the view.
   */
  display: {
    title: string
    itemLabel: string // "Guidance Item" vs "Report Section"
    emptyTitle: string
    emptyDescription: string
  }

  /**
   * Feature flags controlling what's visible.
   */
  features: {
    showScores: boolean
    showFilters: boolean
  }

  /**
   * Custom components to render instead of shared defaults.
   * When provided, these completely replace the shared component.
   */
  components?: {
    ItemForm?: FC<ItemFormProps>
    ItemCard?: FC<ItemCardProps>
  }
}

/**
 * Props for custom item form components.
 */
export interface ItemFormProps {
  disabled: boolean
  saving: boolean
  settings: KnowledgeBaseSettings
  onSubmit: (payload: ItemFormSubmission) => Promise<void>
  onError: (message: string) => void
}

/**
 * Standard submission from item form.
 * Custom forms must produce this structure.
 */
export interface ItemFormSubmission {
  content: string
  score: number | null
  metadata?: Record<string, unknown>
}

/**
 * Props for custom item card components.
 */
export interface ItemCardProps {
  item: KnowledgeBaseItem
  index: number
  disabled: boolean
  onEdit: () => void
  onDelete: () => void
}

/**
 * Default configurations for each variant.
 */
export const FINDINGS_CONFIG: KnowledgeBaseViewConfig = {
  variant: 'findings',
  display: {
    title: 'Knowledge Base',
    itemLabel: 'Guidance Item',
    emptyTitle: 'No guidance items found',
    emptyDescription: 'Add guidance or adjust filters to see matching results.',
  },
  features: {
    showScores: true,
    showFilters: true,
  },
}

export const REPORT_CONFIG: KnowledgeBaseViewConfig = {
  variant: 'report',
  display: {
    title: 'Report Sections',
    itemLabel: 'Report Section',
    emptyTitle: 'No report sections defined',
    emptyDescription: 'Add sections above to define what the LLM should analyze.',
  },
  features: {
    showScores: false,
    showFilters: false,
  },
}
